import { createAsyncThunk } from "@reduxjs/toolkit"

import apiClient from "src/apiClient"
import { facingsByTick } from "src/utils/roundMotion"

import gameReducers from "../reducers"

/* Looking back at a round of the live game (requirement 35, H1–H8).

   The board of round T is the step of tick T from `GET /game/{id}/steps/`: written as that round
   was sent (`record_round_as_sent`), so every march stands at its origin with its road ahead, and
   redacted by the server with the fog as it was then (H3) — nothing here fogs it again. The step
   at the live tick is the live board itself and is never asked for: T at or past it is "come
   back".

   The steps are fetched once and kept for the game, and fetched again when the round wanted is
   newer than anything kept — a round played since the fetch is not in it, and "aged out" for
   yesterday's round because the fetch is old is a lie (P14). A round older than the oldest kept
   genuinely aged out of the retention window, so that never refetches. Module state rather than
   store state: sixty boards are a cache, not something any panel reads.

   **Only a step older than the live round it was fetched in is final** (owner, 2026-10-05, game
   3874). The step of the live tick is the board the last round left, no orders yet, and the
   server rewrites it with that round's orders when the round turns (`record_round_as_sent`). Kept
   as it was, it answered "round 2" a round later with a board that had no marches on it — the
   one place a player looks to see where an enemy stack came from. */
let cache = { key: null, steps: null, liveTick: null }

export const forgetLookBackCache = () => {
  cache = { key: null, steps: null, liveTick: null }
}

const fetchSteps = async (gameId) => {
  const response = await apiClient.get(`/game/${gameId}/steps/`)
  return response.data?.steps || []
}

export const stepsFor = async (gameId, accountId, tick, liveTick) => {
  const key = `${gameId}:${accountId ?? ""}`
  const cached = cache.key === key ? cache.steps : null
  // A round at or past the live tick of the fetch was not over when it was fetched.
  const stale = cached && tick >= cache.liveTick
  if (cached && !stale) return cached
  const steps = await fetchSteps(gameId)
  cache = { key, steps, liveTick }
  return steps
}

// The slice's actions, by type — importing them from `../index` would be a cycle through the
// thunk registry. Plain action creators, so the slice's reducers of the same names run them.
const gameReducersActions = {
  setHistory: (payload) => ({ type: "game/setHistory", payload }),
  clearHistory: () => ({ type: "game/clearHistory" }),
}

export const thunk = createAsyncThunk("game/lookBack", async ({ tick }, { dispatch, getState }) => {
  const state = getState()
  const live = state.game?.game
  if (!live || tick == null || tick < 0 || tick >= live.tick) {
    dispatch(gameReducersActions.clearHistory())
    return null
  }
  // The round is chosen at once — the board is withheld (orders off) while its steps load, so the
  // press is never a click into nothing.
  dispatch(gameReducersActions.setHistory({ tick, game: null, loading: true }))
  try {
    const steps = await stepsFor(live.id, state.user?.id, tick, live.tick)
    const step = steps.find((candidate) => candidate.tick === tick)
    // A pruned step is stored as `{}` on purpose (`GAME_STEP_RETENTION`): there is no board to
    // show, so the report shows its events-only "aged out" line and the live board stays (H7).
    if (!step || Object.keys(step.metadata || {}).length === 0) return { tick, game: null, loading: false }
    // Which way its standing stacks face: what the shows up to that round left them in, as the
    // live board keeps it (`roundMotion.facingsByTick`). The steps are already this viewer's.
    const facings = facingsByTick(steps)[tick] || {}
    return { tick, game: { tick, tickTime: step.datetime ?? null, metadata: step.metadata, facings }, loading: false }
  } catch {
    // A failed fetch reads as aged out rather than as an exception: the shared thunk error handler
    // re-throws inside the reducer, and a dock pane must not take the game view down with it.
    return { tick, game: null, loading: false }
  }
})

export const onSucceed = gameReducers.historyLoaded

const thunkObject = { thunk, onSucceed }
export default thunkObject
